import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, MapPin, Ticket as TicketIcon, AlertCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { ContactCreatorModal } from '@/components/creator/ContactCreatorModal';
import { TicketList } from '@/components/tickets/TicketList';
import { supabase } from '@/lib/supabase/client';
import type { Ticket } from '@/types/tickets';
import { getFirstImageUrl, getCurrencySymbol } from '@/lib/utils/bookingHelpers';

export function BookingsSummary({ bookings, user }: { bookings: any[], user: any }) {
  const navigate = useNavigate();
  const [showContactModal, setShowContactModal] = useState(false);
  const [creatorContact, setCreatorContact] = useState({ email: '', instagram: '', phone: '' });
  const [bookingTickets, setBookingTickets] = useState<Record<string, Ticket[]>>({});
  const [expandedBookings, setExpandedBookings] = useState<Set<string>>(new Set());
  const [expandedTransferredTickets, setExpandedTransferredTickets] = useState<Set<string>>(new Set());
  const [transferredTickets, setTransferredTickets] = useState<any[]>([]);
  const [isLoadingTickets, setIsLoadingTickets] = useState(false);
  const [isLoadingTransferred, setIsLoadingTransferred] = useState(false);

  // Reset local state when user changes to prevent stale data caching
  useEffect(() => {
    setBookingTickets({});
    setTransferredTickets([]);
    setExpandedBookings(new Set());
    setExpandedTransferredTickets(new Set());
  }, [user?.id]);

  // Bookings are pre-filtered to only show confirmed/paid bookings by the parent component
  // Transferred tickets (below) can still show pending/unclaimed status
  const userBookings = useMemo(() => bookings, [bookings]);
  
  // Memoized booking IDs to prevent unnecessary re-fetches
  const bookingIds = useMemo(() => 
    userBookings.map(booking => booking.id).sort().join(','), 
    [userBookings]
  );

  // Fetch tickets for all bookings - optimized with proper dependencies
  useEffect(() => {
    const fetchTickets = async () => {
      if (userBookings.length === 0) {
        setBookingTickets({});
        return;
      }
      
      setIsLoadingTickets(true);
      
      try {
        const ticketPromises = userBookings.map(async (booking) => {
          const { data, error } = await supabase
            .from('tickets')
            .select('*')
            .eq('booking_id', booking.id)
            .order('ticket_number');
          
          if (!error && data) {
            return { bookingId: booking.id, tickets: data };
          }
          return null;
        });
        
        const results = await Promise.all(ticketPromises);
        const ticketMap: Record<string, Ticket[]> = {};
        
        results.forEach(result => {
          if (result) {
            ticketMap[result.bookingId] = result.tickets;
          }
        });
        
        setBookingTickets(ticketMap);
      } catch (error) {
        console.error('Error fetching tickets:', error);
      } finally {
        setIsLoadingTickets(false);
      }
    };
    
    fetchTickets();
  }, [bookingIds]);

  // Fetch allocated/transferred tickets - optimized with proper dependencies
  useEffect(() => {
    const fetchTransferredTickets = async () => {
      if (!user?.email) {
        setTransferredTickets([]);
        return;
      }

      setIsLoadingTransferred(true);

      try {
        const { data: tickets, error } = await supabase
          .from('tickets')
          .select(`
            *,
            experience:experiences!tickets_experience_id_fkey (
              id,
              title,
              description,
              location,
              status,
              media:experience_media (
                url,
                type,
                order_index
              )
            )
          `)
          .eq('guest_email', user.email)
          .not('transfer_type', 'is', null); // Only get tickets with transfer_type set
        
        if (error) {
          console.error('Error fetching transferred tickets:', error);
          setTransferredTickets([]);
        } else if (tickets) {
          console.log('Transferred tickets found:', tickets.length, tickets);
          setTransferredTickets(tickets);
        }
      } catch (error) {
        console.error('Error fetching transferred tickets:', error);
        setTransferredTickets([]);
      } finally {
        setIsLoadingTransferred(false);
      }
    };

    fetchTransferredTickets();
  }, [user?.email]);
  
  const toggleBooking = useCallback((bookingId: string) => {
    setExpandedBookings(prev => {
      const newExpanded = new Set(prev);
      if (newExpanded.has(bookingId)) {
        newExpanded.delete(bookingId);
      } else {
        newExpanded.add(bookingId);
      }
      return newExpanded;
    });
  }, []);

  const toggleTransferredTicket = useCallback((ticketId: string) => {
    setExpandedTransferredTickets(prev => {
      const newExpanded = new Set(prev);
      if (newExpanded.has(ticketId)) {
        newExpanded.delete(ticketId);
      } else {
        newExpanded.add(ticketId);
      }
      return newExpanded;
    });
  }, []);

  // Memoized ticket refetch function to prevent unnecessary re-renders
  const refetchTicketsForBooking = useCallback((bookingId: string) => {
    supabase
      .from('tickets')
      .select('*')
      .eq('booking_id', bookingId)
      .order('ticket_number')
      .then(({ data }) => {
        if (data) {
          setBookingTickets(prev => ({
            ...prev,
            [bookingId]: data
          }));
        }
      });
  }, []);

  return (
    <div className="bg-earth-800/50 p-4 sm:p-6 rounded-lg">
      <div className="flex justify-between items-center mb-4 sm:mb-6">
        <h2 className="text-lg sm:text-xl font-display">My Experiences & Tickets</h2>
        <Button 
          variant="outline" 
          size="sm"
          onClick={() => navigate('/bookings')}
          className="text-xs sm:text-sm"
        >
          View All
        </Button>
      </div>

      {(userBookings.length > 0 || transferredTickets.length > 0) ? (
        <div className="space-y-4">
          {/* Transferred/Allocated Tickets Section */}
          {transferredTickets.length > 0 && (
            <div className="mb-4">
              <h3 className="text-sm font-medium text-sand-300 mb-2">Tickets Shared With You</h3>
              <div className="space-y-2">
                {transferredTickets.map((ticket) => {
                  const experience = ticket.experience;
                  if (!experience) return null;

                  const isExpanded = expandedTransferredTickets.has(ticket.id);

                  return (
                    <div 
                      key={ticket.id}
                      className={`rounded-lg overflow-hidden border ${
                        ticket.transfer_type === 'transfer' 
                          ? 'bg-blue-900/20 border-blue-700/30' 
                          : 'bg-green-900/20 border-green-700/30'
                      }`}
                    >
                      <div className="p-3 sm:p-4">
                        <div className="flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-4">
                          <div className="w-full sm:w-20 h-32 sm:h-20 rounded-lg overflow-hidden flex-shrink-0">
                            <img
                              src={getFirstImageUrl(experience)}
                              alt={experience.title}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-medium text-sand-100 mb-2 text-sm sm:text-base">{experience.title}</h3>
                            <div className="flex flex-wrap items-center gap-2 text-xs text-sand-400 mb-2">
                              <div className="flex items-center gap-1">
                                <TicketIcon className="h-3 w-3 sm:h-4 sm:w-4" />
                                <span>Ticket #{ticket.ticket_number}</span>
                              </div>
                              <span className="text-amber-400">
                                {ticket.transfer_type === 'transfer' ? 'Transferred' : 'Allocated'} to you
                              </span>
                            </div>
                            <div className="flex flex-wrap gap-2 mt-2">
                              {!ticket.claimed_at && (
                                <Button
                                  size="sm"
                                  variant="primary"
                                  onClick={() => navigate('/bookings')}
                                  className="bg-amber-400 text-earth-900 hover:bg-amber-300 text-xs"
                                >
                                  <AlertCircle className="h-3 w-3 mr-1" />
                                  Claim Ticket
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => toggleTransferredTicket(ticket.id)}
                                className="text-xs"
                              >
                                {isExpanded ? (
                                  <>
                                    <ChevronUp className="h-3 w-3 mr-1" />
                                    Hide Details
                                  </>
                                ) : (
                                  <>
                                    <ChevronDown className="h-3 w-3 mr-1" />
                                    View Details
                                  </>
                                )}
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Expanded Details */}
                      {isExpanded && (
                        <div className="border-t border-earth-700/50 p-3 sm:p-4 space-y-2">
                          <div className="text-sm text-sand-400">
                            <p><strong>Guest Name:</strong> {ticket.guest_name || 'Not specified'}</p>
                            <p><strong>Guest Email:</strong> {ticket.guest_email}</p>
                            <p><strong>Status:</strong> {ticket.claimed_at ? 'Claimed' : 'Pending Claim'}</p>
                            {ticket.checked_in && (
                              <p><strong>Checked In:</strong> Yes</p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Regular Bookings Section */}
          {userBookings.length > 0 && transferredTickets.length > 0 && (
            <h3 className="text-sm font-medium text-sand-300 mb-2">Your Bookings</h3>
          )}
          {userBookings.map((booking) => {
            // Use the experience data from the booking (fetched with proper relations)
            const experience = booking.experience;
            if (!experience) return null;

            const tickets = bookingTickets[booking.id] || [];
            const activeTickets = tickets.filter(t => t.transfer_type !== 'transfer');
            const transferredTicketsForBooking = tickets.filter(t => t.transfer_type === 'transfer');
            const unclaimedTickets = activeTickets.filter(t => t.guest_email && !t.claimed_at);
            const hasTickets = tickets.length > 0; // Remove confirmed status requirement
            
            return (
              <div 
                key={booking.id}
                className="bg-earth-800 rounded-lg overflow-hidden"
              >
                <div className="p-3 sm:p-4">
                  <div className="flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-4">
                    <div className="w-full sm:w-20 h-32 sm:h-20 rounded-lg overflow-hidden flex-shrink-0">
                      <img
                        src={getFirstImageUrl(experience)}
                        alt={experience.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-sand-100 mb-1 text-sm sm:text-base">{experience.title}</h3>
                      <p className="text-sm font-semibold text-sand-300 mb-2">
                        {getCurrencySymbol(experience.pricing_config?.currency)}{booking.total_amount}
                      </p>
                      
                      {/* Mobile Status and Location */}
                      <div className="flex flex-wrap items-center gap-2 mb-2 sm:hidden">
                        <div className={`px-2 py-1 rounded-full text-xs ${
                          booking.status === 'confirmed' ? 'bg-green-500/20 text-green-400' :
                          booking.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                          booking.status === 'cancelled' ? 'bg-red-500/20 text-red-400' :
                          'bg-gray-500/20 text-gray-400'
                        }`}>
                          {booking.status === 'pending' ? 'Pending Payment' : 
                           booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}
                        </div>
                        {experience.location && (
                          <div className="flex items-center gap-1 text-xs text-sand-400">
                            <MapPin className="h-3 w-3 flex-shrink-0" />
                            <span className="truncate">{experience.location.city || experience.location.name}</span>
                          </div>
                        )}
                      </div>
                      
                      {experience.creator && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="mt-2 text-xs sm:text-sm"
                          onClick={e => {
                            e.stopPropagation();
                            setCreatorContact({
                              email: experience.creator?.creator_profile?.contact_email || 'info@letsrendezvous.co',
                              instagram: experience.creator?.creator_profile?.social_links?.instagram || 'https://www.instagram.com/letsrendez.vous/',
                              phone: (experience.creator && 'phone' in experience.creator && (experience.creator as any).phone) || '',
                            });
                            setShowContactModal(true);
                          }}
                        >
                          Contact {experience.creator.creator_profile?.business_name || experience.creator.full_name || 'Creator'}
                        </Button>
                      )}
                    </div>
                    
                    {/* Desktop Status and Location */}
                    <div className="hidden sm:flex flex-col items-end gap-2 min-w-[120px]">
                      <div className={`px-2 py-1 rounded-full text-xs ${
                        booking.status === 'confirmed' ? 'bg-green-500/20 text-green-400' :
                        booking.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                        booking.status === 'cancelled' ? 'bg-red-500/20 text-red-400' :
                        'bg-gray-500/20 text-gray-400'
                      }`}>
                        {booking.status === 'pending' ? 'Pending Payment' : 
                         booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}
                      </div>
                      {experience.location && (
                        <div className="flex items-center gap-2 text-xs text-sand-400">
                          <MapPin className="h-4 w-4 flex-shrink-0" />
                          <span className="truncate">{experience.location.city || experience.location.name}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Tickets Section - Mobile Optimized */}
                  {hasTickets && (
                    <div className="flex flex-wrap items-center justify-between gap-2 mt-3 pt-3 border-t border-earth-700/50">
                      <div className="flex flex-wrap items-center gap-2">
                        <Button
                          size="sm"
                          variant="primary"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleBooking(booking.id);
                          }}
                          className="bg-sand-400 text-earth-900 hover:bg-sand-300 text-xs"
                        >
                          <TicketIcon className="h-3 w-3 mr-1" />
                          {activeTickets.length} Active
                        </Button>
                        {transferredTicketsForBooking.length > 0 && (
                          <span className="text-xs text-sand-400">
                            {transferredTicketsForBooking.length} transferred
                          </span>
                        )}
                      </div>
                      {unclaimedTickets.length > 0 && (
                        <span className="flex items-center gap-1 text-xs text-amber-400 font-medium">
                          <AlertCircle className="h-3 w-3 animate-pulse" />
                          {unclaimedTickets.length} unclaimed
                        </span>
                      )}
                    </div>
                  )}
                </div>
                
                {/* Expanded Tickets Section */}
                {hasTickets && expandedBookings.has(booking.id) && (
                  <div className="border-t border-earth-700 p-3 sm:p-4">
                    <TicketList
                      tickets={tickets}
                      experienceTitle={experience.title}
                      experienceDate={experience.start_date}
                      onUpdate={() => refetchTicketsForBooking(booking.id)}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-6">
          <p className="text-sand-400 mb-4 text-sm">No experiences or tickets yet</p>
          <Button onClick={() => navigate('/experiences')} size="sm">
            Explore Experiences
          </Button>
        </div>
      )}
      <ContactCreatorModal
        isOpen={showContactModal}
        onClose={() => setShowContactModal(false)}
        email={creatorContact.email}
        instagram={creatorContact.instagram}
        phone={creatorContact.phone}
      />
    </div>
  );
}